import { createApiUrl } from '../utils/apiConfig';
import tokenStorage from '../utils/tokenStorage';
import { handleApiResponse, createApiRequest } from '../utils/apiResponseHandler';

const defaultHeaders = {
  'Content-Type': 'application/json',
};

function buildHeaders(extra = {}) {
  const headers = { ...defaultHeaders, ...extra };

  // First try to get token from localStorage
  let token = tokenStorage.getAccessToken();

  // If no token in localStorage, check cookies
  if (!token) {
    const cookies = document.cookie.split(';');
    const accessTokenCookie = cookies.find(c => c.trim().startsWith('accessToken='));
    if (accessTokenCookie) {
      token = accessTokenCookie.split('=')[1]?.trim();
    }
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

async function handleResponse(resp, endpoint) {
  try {
    // Use the standardized response handler
    const result = await handleApiResponse(resp);

    // For 401 errors, just return the error
    // tokenManager will handle automatic refresh via its timer
    // Components should handle 401 by checking auth state or redirecting to login

    return result;
  } catch (error) {
    // If fetch itself failed (connection error), handle it here
    const errorMsg = error.message || String(error);
    const errorString = JSON.stringify(error).toLowerCase();
    const isConnectionError =
      errorMsg.includes('ERR_CONNECTION_REFUSED') ||
      errorMsg.includes('Failed to fetch') ||
      errorMsg.includes('NetworkError') ||
      errorMsg.includes('ECONNREFUSED') ||
      errorString.includes('connection_refused') ||
      errorString.includes('failed to fetch') ||
      errorString.includes('networkerror');

    if (isConnectionError) {
      return {
        success: false,
        error: 'Backend server not available',
        message: 'Cannot connect to server',
        data: null,
      };
    }
    throw error;
  }
}

export const apiClient = {
  get: async (endpoint, options = {}) => {
    try {
      const resp = await fetch(createApiUrl(endpoint), {
        method: 'GET',
        credentials: 'include',
        headers: buildHeaders(options.headers),
      });
      return handleResponse(resp, endpoint, options);
    } catch (error) {
      // Handle fetch-level errors (connection refused, network errors, etc.)
      const errorMsg = error.message || String(error);
      const errorString = JSON.stringify(error).toLowerCase();
      const isConnectionError =
        errorMsg.includes('ERR_CONNECTION_REFUSED') ||
        errorMsg.includes('Failed to fetch') ||
        errorMsg.includes('NetworkError') ||
        errorMsg.includes('ECONNREFUSED') ||
        errorString.includes('connection_refused') ||
        errorString.includes('failed to fetch') ||
        errorString.includes('networkerror');

      if (isConnectionError) {
        return {
          success: false,
          error: 'Backend server not available',
          message: 'Cannot connect to server',
          data: null,
        };
      }
      throw error;
    }
  },
  post: async (endpoint, data = {}, options = {}) => {
    try {
      const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
      const headers = buildHeaders(options.headers);
      // IMPORTANT: When sending FormData we must NOT set Content-Type
      // manually. Let the browser set the correct multipart boundary.
      if (isFormData && headers['Content-Type']) {
        delete headers['Content-Type'];
      }

      const resp = await fetch(createApiUrl(endpoint), {
        method: 'POST',
        credentials: 'include',
        headers,
        body: isFormData ? data : JSON.stringify(data),
      });
      return handleResponse(resp, endpoint, options);
    } catch (error) {
      // Handle fetch-level errors (connection refused, network errors, etc.)
      const errorMsg = error.message || String(error);
      const errorString = JSON.stringify(error).toLowerCase();
      const isConnectionError =
        errorMsg.includes('ERR_CONNECTION_REFUSED') ||
        errorMsg.includes('Failed to fetch') ||
        errorMsg.includes('NetworkError') ||
        errorMsg.includes('ECONNREFUSED') ||
        errorString.includes('connection_refused') ||
        errorString.includes('failed to fetch') ||
        errorString.includes('networkerror');

      if (isConnectionError) {
        return {
          success: false,
          error: 'Backend server not available',
          message: 'Cannot connect to server',
          data: null,
        };
      }
      throw error;
    }
  },
  put: async (endpoint, data = {}, options = {}) => {
    try {
      const resp = await fetch(createApiUrl(endpoint), {
        method: 'PUT',
        credentials: 'include',
        headers: buildHeaders(options.headers),
        body: JSON.stringify(data),
      });
      return handleResponse(resp, endpoint, options);
    } catch (error) {
      const errorMsg = error.message || String(error);
      const errorString = JSON.stringify(error).toLowerCase();
      const isConnectionError =
        errorMsg.includes('ERR_CONNECTION_REFUSED') ||
        errorMsg.includes('Failed to fetch') ||
        errorMsg.includes('NetworkError') ||
        errorMsg.includes('ECONNREFUSED') ||
        errorString.includes('connection_refused') ||
        errorString.includes('failed to fetch') ||
        errorString.includes('networkerror');

      if (isConnectionError) {
        return {
          success: false,
          error: 'Backend server not available',
          message: 'Cannot connect to server',
          data: null,
        };
      }
      throw error;
    }
  },
  del: async (endpoint, options = {}) => {
    try {
      const resp = await fetch(createApiUrl(endpoint), {
        method: 'DELETE',
        credentials: 'include',
        headers: buildHeaders(options.headers),
      });
      return handleResponse(resp, endpoint, options);
    } catch (error) {
      const errorMsg = error.message || String(error);
      const errorString = JSON.stringify(error).toLowerCase();
      const isConnectionError =
        errorMsg.includes('ERR_CONNECTION_REFUSED') ||
        errorMsg.includes('Failed to fetch') ||
        errorMsg.includes('NetworkError') ||
        errorMsg.includes('ECONNREFUSED') ||
        errorString.includes('connection_refused') ||
        errorString.includes('failed to fetch') ||
        errorString.includes('networkerror');

      if (isConnectionError) {
        return {
          success: false,
          error: 'Backend server not available',
          message: 'Cannot connect to server',
          data: null,
        };
      }
      throw error;
    }
  },
};

// Enhanced API client using the standardized response handler
export const standardizedApiClient = {
  get: async (endpoint, options = {}, dataPath = null) => {
    return await createApiRequest(
      createApiUrl(endpoint),
      {
        method: 'GET',
        credentials: 'include',
        headers: buildHeaders(options.headers),
        ...options,
      },
      dataPath
    );
  },
  post: async (endpoint, data = {}, options = {}, dataPath = null) => {
    return await createApiRequest(
      createApiUrl(endpoint),
      {
        method: 'POST',
        credentials: 'include',
        headers: buildHeaders(options.headers),
        body: JSON.stringify(data),
        ...options,
      },
      dataPath
    );
  },
  put: async (endpoint, data = {}, options = {}, dataPath = null) => {
    return await createApiRequest(
      createApiUrl(endpoint),
      {
        method: 'PUT',
        credentials: 'include',
        headers: buildHeaders(options.headers),
        body: JSON.stringify(data),
        ...options,
      },
      dataPath
    );
  },
  delete: async (endpoint, options = {}, dataPath = null) => {
    return await createApiRequest(
      createApiUrl(endpoint),
      {
        method: 'DELETE',
        credentials: 'include',
        headers: buildHeaders(options.headers),
        ...options,
      },
      dataPath
    );
  },
};

// Export both clients for backward compatibility
export default apiClient;
export { standardizedApiClient as apiClientV2 };
